import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch } from '@/redux/Store';
import { toggleCommentLike, toggleCommentDislike } from '@/redux/thunks/commentThunks';
import { toggleFollow } from '@/redux/thunks/dashboardThunks';
import { useSignupGuard } from '@/hooks/useSignupGuard';
import Comment, { CommentData } from './Comment';
import './CommentList.scss';

interface CommentListProps {
  postId: string;
  currentUserId?: string;
  onFetchComments: (postId: string, limit: number, offset: number) => Promise<{
    comments: CommentData[];
    hasMore: boolean;
  }>;
  onCreateComment: (postId: string, body: string) => Promise<CommentData>;
  onUpdateComment: (commentId: string, body: string) => Promise<CommentData>;
  onDeleteComment: (commentId: string) => Promise<void>;
  followingUserIds?: string[]; // List of user IDs the current user is following
  onFollowToggle?: (userId: string, isNowFollowing: boolean) => void; // Callback when follow is toggled
}

const CommentList: React.FC<CommentListProps> = ({
  postId,
  currentUserId,
  onFetchComments,
  onCreateComment,
  onUpdateComment,
  onDeleteComment,
  followingUserIds = [],
  onFollowToggle,
}) => {
  const dispatch = useAppDispatch();
  const { requireAuth } = useSignupGuard();
  const [comments, setComments] = useState<CommentData[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  const limit = 20;

  // Initial load
  useEffect(() => {
    loadComments(0);
  }, [postId]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadComments(offset);
        }
      },
      { threshold: 0.5 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoading, offset]);

  const loadComments = async (currentOffset: number) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const result = await onFetchComments(postId, limit, currentOffset);
      if (currentOffset === 0) {
        setComments(result.comments);
      } else {
        setComments((prev) => [...prev, ...result.comments]);
      }
      setHasMore(result.hasMore);
      setOffset(currentOffset + result.comments.length);
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;
    if (!requireAuth('Sign up to comment')) return;

    setIsSubmitting(true);
    try {
      const comment = await onCreateComment(postId, newComment.trim());
      // Append to end since comments are sorted oldest to newest
      setComments((prev) => [...prev, comment]);
      setNewComment('');
    } catch (error) {
      console.error('Failed to create comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (commentId: string, newBody: string) => {
    try {
      const updated = await onUpdateComment(commentId, newBody);
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? updated : c))
      );
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };

  const handleDelete = async (commentId: string) => {
    try {
      await onDeleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (error) {
      console.error('Failed to delete comment:', error);
      throw error;
    }
  };

  const handleLike = async (commentId: string) => {
    try {
      const result = await dispatch(toggleCommentLike(commentId)).unwrap();
      // Update local comment state with new reaction counts
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? {
                ...c,
                likeCount: result.likeCount,
                dislikeCount: result.dislikeCount,
                userLiked: result.liked,
                userDisliked: result.disliked,
              }
            : c
        )
      );
    } catch (error) {
      console.error('Failed to like comment:', error);
    }
  };

  const handleDislike = async (commentId: string) => {
    try {
      const result = await dispatch(toggleCommentDislike(commentId)).unwrap();
      // Update local comment state with new reaction counts
      setComments((prev) =>
        prev.map((c) =>
          c.id === commentId
            ? {
                ...c,
                likeCount: result.likeCount,
                dislikeCount: result.dislikeCount,
                userLiked: result.liked,
                userDisliked: result.disliked,
              }
            : c
        )
      );
    } catch (error) {
      console.error('Failed to dislike comment:', error);
    }
  };

  const handleFollow = async (userId: string) => {
    try {
      const wasFollowing = followingUserIds.includes(userId);
      await dispatch(toggleFollow({ targetUserId: userId })).unwrap();
      const isNowFollowing = !wasFollowing;
      // Notify parent component to update followingUserIds
      if (onFollowToggle) {
        onFollowToggle(userId, isNowFollowing);
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    }
  };

  return (
    <div className="comment-list">
      {/* Comments List */}
      <div className="comments">
        {comments.length === 0 && !isLoading ? (
          <div className="empty-state">
            <p>No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          <>
            {comments.map((comment) => (
              <Comment
                key={comment.id}
                comment={comment}
                currentUserId={currentUserId}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onLike={handleLike}
                onDislike={handleDislike}
                onFollow={handleFollow}
                isFollowing={followingUserIds.includes(comment.userId)}
              />
            ))}

            {/* Infinite scroll trigger */}
            {hasMore && <div ref={observerTarget} className="load-more-trigger" />}

            {isLoading && (
              <div className="loading-indicator">
                <div className="spinner"></div>
                <span>Loading comments...</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Inline composer — single click to start typing. Collapses
          to a one-line "Add a comment…" field; on focus the textarea
          grows + the Post button + char counter slide in. Cmd/Ctrl+Enter
          submits without reaching for the mouse. */}
      <form
        className={`comment-composer ${isComposerFocused || newComment.length > 0 ? 'is-active' : ''}`}
        onSubmit={handleSubmit}
      >
        <textarea
          className="comment-composer__field"
          placeholder="Add a comment…"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onFocus={() => setIsComposerFocused(true)}
          onBlur={() => setIsComposerFocused(false)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              if (newComment.trim() && !isSubmitting) {
                handleSubmit(e as unknown as React.FormEvent);
              }
            }
          }}
          maxLength={500}
          rows={1}
          disabled={isSubmitting}
        />
        <div className="comment-composer__footer">
          <span className="comment-composer__count">
            {500 - newComment.length} characters remaining
          </span>
          <button
            type="submit"
            className="comment-composer__submit"
            disabled={!newComment.trim() || isSubmitting}
          >
            {isSubmitting ? 'Posting…' : 'Post'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommentList;
